import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { BarList, ColumnChart, Heatmap, LineChart, type ChartDatum, type SeriesDef, type Slot } from "@/components/ui/chart";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/feedback";
import { OutcomeDot } from "@/components/ui/outcome";
import { groupSeries, NO_GROUP, type OutcomeLook, useOutcomeLook } from "@/features/outcome-look";
import type { TabProps } from "@/features/report-lines";
import {
  DataTable, Figures, KpiRow, KpiTile, NONE, ReportSection, ShareBar, tableExport, type DataColumn,
} from "@/features/report-kit";
import { groupTerm } from "@/lib/dispositions";
import { useReport, useReportOptions } from "@/lib/queries";
import {
  MISSED_LABELS, STATE_GROUPS, breakLabel, bucketLabel, bucketTitle, delta, fmtClock, fmtCompact, fmtCount, fmtHours,
  fmtPct, fmtSeconds, labelOf, stateGroups, type ReportFilters,
} from "@/lib/reports";
import type { RepReport, RepReportRow } from "@/lib/types";
import { cn } from "@/lib/utils";

const STATE_SERIES: SeriesDef[] = STATE_GROUPS.map((g) => ({ key: g.key, label: g.label, slot: g.slot }));

function repName(r: { name: string | null; user: string }): string {
  return r.name ?? `Extension ${r.user}`;
}

function mosTone(mos: number | null): "success" | "warning" | "danger" | "neutral" {
  if (mos == null) return "neutral";
  if (mos >= 4) return "success";
  if (mos >= 3.5) return "warning";
  return "danger";
}

function leaderboardColumns(skillNames: Map<string, string>): DataColumn<RepReportRow>[] {
  return [
    {
      key: "name",
      header: "Rep",
      value: (r) => repName(r),
      render: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-text">{repName(r)}</span>
          <span className="text-2xs text-text-subtle">
            {r.user}
            {r.skills.length ? ` · ${r.skills.map((s) => skillNames.get(s) ?? s).join(", ")}` : ""}
          </span>
        </span>
      ),
    },
    { key: "calls", header: "Calls", align: "right", value: (r) => r.calls, render: (r) => fmtCount(r.calls) },
    {
      key: "mix",
      header: "In · Out · Handoff",
      align: "right",
      value: (r) => `${r.inbound}/${r.outbound}/${r.handoff}`,
      sortable: false,
      render: (r) => (
        <span className="text-text-muted">
          {r.inbound} · {r.outbound} · {r.handoff}
        </span>
      ),
    },
    { key: "talk_total", header: "Talk", align: "right", value: (r) => r.talk_total, render: (r) => fmtHours(r.talk_total) },
    { key: "talk_avg", header: "Avg talk", align: "right", value: (r) => r.talk_avg, render: (r) => fmtSeconds(r.talk_avg) },
    {
      key: "handle_avg",
      header: "Avg handle",
      align: "right",
      title: "Talk plus the disposition window and wrap-up, per call",
      value: (r) => r.handle_avg,
      render: (r) => fmtSeconds(r.handle_avg),
    },
    { key: "after_call_avg", header: "After-call", align: "right", value: (r) => r.after_call_avg, render: (r) => fmtSeconds(r.after_call_avg) },
    {
      key: "occupancy",
      header: "Occupancy",
      align: "right",
      title: "Time working over time available to work (tap-ucaas's definition). A break counts as neither.",
      value: (r) => r.occupancy,
      render: (r) => (
        <>
          {fmtPct(r.occupancy)}
          <ShareBar value={r.occupancy} />
        </>
      ),
    },
    { key: "calls_per_hour", header: "Calls / hr", align: "right", value: (r) => r.calls_per_hour, render: (r) => (r.calls_per_hour == null ? "—" : r.calls_per_hour.toFixed(1)) },
    {
      key: "missed_offers",
      header: "Missed",
      align: "right",
      title: "Offers the rep let ring out, declined, or that could not ring their phone",
      value: (r) => r.missed_offers,
      render: (r) => (r.missed_offers > 0 ? <Badge tone="warning" size="sm">{fmtCount(r.missed_offers)}</Badge> : "0"),
    },
    { key: "outcomes", header: "Outcomes", align: "right", value: (r) => r.outcomes },
    { key: "success", header: "Successes", align: "right", value: (r) => r.success },
    { key: "success_rate", header: "Success %", align: "right", value: (r) => r.success_rate, render: (r) => fmtPct(r.success_rate) },
    { key: "handoffs_sent", header: "Handed off", align: "right", value: (r) => r.handoffs_sent },
    { key: "handoffs_taken", header: "Took handoffs", align: "right", value: (r) => r.handoffs_taken },
    { key: "dials", header: "Own dials", align: "right", value: (r) => r.dials },
    { key: "signed_in", header: "Signed in", align: "right", value: (r) => r.signed_in_seconds, render: (r) => fmtHours(r.signed_in_seconds) },
    {
      key: "mos_avg",
      header: "Call quality",
      align: "right",
      title: "Average MOS on the rep's side of their calls (1 to 5): their network and headset",
      value: (r) => r.mos_avg,
      render: (r) =>
        r.mos_avg == null ? (
          "—"
        ) : (
          <Badge tone={mosTone(r.mos_avg)} size="sm">
            {r.mos_avg.toFixed(2)}
          </Badge>
        ),
    },
    { key: "poor_calls", header: "Poor calls", align: "right", value: (r) => r.poor_calls },
  ];
}

type Sheet = RepReport["timesheet"][number];

function timesheetColumns(tz: string): DataColumn<Sheet>[] {
  return [
    { key: "day", header: "Day", value: (r) => r.day },
    { key: "name", header: "Rep", value: (r) => repName(r) },
    { key: "first_in", header: "First in", value: (r) => r.first_in, render: (r) => fmtClock(r.first_in, tz) },
    { key: "last_out", header: "Last out", value: (r) => r.last_out, render: (r) => fmtClock(r.last_out, tz) },
    { key: "signed_in", header: "Signed in", align: "right", value: (r) => r.signed_in_seconds, render: (r) => fmtHours(r.signed_in_seconds) },
    {
      key: "breaks",
      header: "Breaks",
      align: "right",
      value: (r) => Object.values(r.break_seconds).reduce((a, b) => a + b, 0),
      render: (r) => {
        const total = Object.values(r.break_seconds).reduce((a, b) => a + b, 0);
        return (
          <span title={Object.entries(r.break_seconds).map(([c, s]) => `${breakLabel(c)} ${fmtSeconds(s)}`).join(", ")}>
            {total ? fmtSeconds(total) : "—"}
          </span>
        );
      },
    },
    { key: "calls", header: "Calls", align: "right", value: (r) => r.calls },
    { key: "talk", header: "Talk", align: "right", value: (r) => r.talk_total, render: (r) => fmtHours(r.talk_total) },
  ];
}

/** Break codes as series: the colour follows the code, in a stable (alphabetical) order. */
function breakSeries(rows: readonly RepReportRow[]): SeriesDef[] {
  const codes = [...new Set(rows.flatMap((r) => Object.keys(r.break_seconds)))].sort();
  return codes.map((code, i) => ({ key: code, label: breakLabel(code), slot: (i < 7 ? i + 1 : "other") as Slot }));
}

export function RepReportView({ query, filters, compareText, onDrill, onTruncated }: TabProps) {
  const report = useReport<RepReport>("reps", query);
  const options = useReportOptions();
  const data = report.data;
  const loading = report.isPending;
  React.useEffect(() => onTruncated("reps", Boolean(data?.truncated)), [data?.truncated, onTruncated]);
  const [open, setOpen] = React.useState<RepReportRow | null>(null);
  const skillNames = React.useMemo(() => new Map((options.data?.skills ?? []).map((s) => [s.slug, s.name])), [options.data]);
  const outcomeNames = React.useMemo(
    () => new Map((options.data?.dispositions ?? []).map((d) => [d.slug, d.label])),
    [options.data],
  );
  // Each outcome's group, for its colour (ACL-508): the report's options name outcomes, and the
  // tenant's catalog, which every campaign picks from, holds their groups.
  const look = useOutcomeLook(null);
  const g = data?.window.granularity ?? "day";
  const team = data?.team;
  const prev = data?.previous ?? null;
  const reps = data?.reps ?? NONE;
  const board = React.useMemo(() => leaderboardColumns(skillNames), [skillNames]);
  const sheetColumns = React.useMemo(() => timesheetColumns(filters.tz), [filters.tz]);
  const breaks = React.useMemo(() => breakSeries(reps), [reps]);

  const topOutcomes = React.useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of reps) for (const [slug, n] of Object.entries(r.outcome_mix)) totals.set(slug, (totals.get(slug) ?? 0) + n);
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([slug]) => slug);
  }, [reps]);
  const outcomeColumns: DataColumn<RepReportRow>[] = React.useMemo(
    () => [
      { key: "name", header: "Rep", value: (r) => repName(r) },
      { key: "outcomes", header: "Outcomes", align: "right", value: (r) => r.outcomes },
      ...topOutcomes.map((slug) => {
        const group = look(slug).group;
        return {
          key: `o:${slug}`,
          header: outcomeNames.get(slug) ?? slug,
          mark: group ? <OutcomeDot group={group} /> : undefined,
          align: "right" as const,
          value: (r: RepReportRow) => r.outcome_mix[slug] ?? 0,
        };
      }),
      {
        key: "other",
        header: "Other",
        align: "right",
        value: (r) => r.outcomes - topOutcomes.reduce((acc, slug) => acc + (r.outcome_mix[slug] ?? 0), 0),
      },
      { key: "success_rate", header: "Success %", align: "right", value: (r) => r.success_rate, render: (r) => fmtPct(r.success_rate) },
      {
        key: "outcome_rate",
        header: "Calls with an outcome",
        align: "right",
        value: (r) => r.outcome_rate,
        render: (r) => fmtPct(r.outcome_rate),
      },
    ],
    [topOutcomes, outcomeNames, look],
  );

  const exports = {
    board: React.useMemo(() => tableExport(board, reps), [board, reps]),
    sheet: React.useMemo(() => tableExport(sheetColumns, data?.timesheet ?? []), [sheetColumns, data?.timesheet]),
    outcomes: React.useMemo(() => tableExport(outcomeColumns, reps), [outcomeColumns, reps]),
    time: React.useMemo(
      () =>
        tableExport(
          [
            { key: "name", header: "Rep", value: (r: RepReportRow) => repName(r) },
            ...STATE_GROUPS.map((grp) => ({ key: grp.key, header: `${grp.label} (h)`, value: (r: RepReportRow) => +(stateGroups(r.state_seconds)[grp.key]! / 3600).toFixed(2) })),
            { key: "offline", header: "Offline (h)", value: (r: RepReportRow) => +((r.state_seconds.offline ?? 0) / 3600).toFixed(2) },
          ],
          reps,
        ),
      [reps],
    ),
    breaks: React.useMemo(
      () =>
        tableExport(
          [
            { key: "name", header: "Rep", value: (r: RepReportRow) => repName(r) },
            ...breaks.map((b) => ({ key: b.key, header: `${b.label} (min)`, value: (r: RepReportRow) => Math.round((r.break_seconds[b.key] ?? 0) / 60) })),
          ],
          reps,
        ),
      [reps, breaks],
    ),
  };

  const points: ChartDatum[] = (data?.series ?? []).map((s) => ({
    key: s.start,
    label: bucketLabel(s.start, g),
    title: bucketTitle(s.start, g),
    values: { calls: s.calls, occupancy: s.occupancy },
  }));

  return (
    <div className="space-y-4">
      <KpiRow>
        <KpiTile label="Reps" loading={loading} value={fmtCount(team?.reps)} hint="signed in or took a call" delta={delta(team?.reps, prev?.reps, "count", "neutral")} deltaContext={compareText} />
        <KpiTile
          label="Calls taken"
          loading={loading}
          value={fmtCompact(team?.calls)}
          hint={team?.calls_per_hour != null ? `${team.calls_per_hour.toFixed(1)} an hour per rep` : undefined}
          delta={delta(team?.calls, prev?.calls, "count", "neutral")}
          deltaContext={compareText}
          spark={(data?.series ?? []).map((s) => s.calls)}
        />
        <KpiTile
          label="Average handle time"
          loading={loading}
          value={fmtSeconds(team?.handle_avg)}
          hint={team ? `${fmtSeconds(team.talk_avg)} talk + ${fmtSeconds(team.after_call_avg)} after` : undefined}
          delta={delta(team?.handle_avg, prev?.handle_avg, "seconds", "neutral")}
          deltaContext={compareText}
          info="Talk, plus the disposition window and wrap-up, per call taken."
        />
        <KpiTile
          label="Occupancy"
          loading={loading}
          value={fmtPct(team?.occupancy)}
          hint="of the time available to work"
          delta={delta(team?.occupancy, prev?.occupancy, "rate", "neutral")}
          deltaContext={compareText}
          spark={(data?.series ?? []).map((s) => s.occupancy)}
          info="Time on calls, dialing, in the disposition window or wrap-up, over that plus time available or ringing. Breaks are left out."
        />
        <KpiTile
          label="Time signed in"
          loading={loading}
          value={fmtHours(team?.signed_in_seconds)}
          hint={team ? `${fmtHours(team.state_seconds.paused ?? 0)} of it on break` : undefined}
          delta={delta(team?.signed_in_seconds, prev?.signed_in_seconds, "seconds", "neutral")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Missed offers"
          loading={loading}
          value={fmtCount(team?.missed_offers)}
          hint="calls a rep let ring out"
          delta={delta(team?.missed_offers, prev?.missed_offers, "count", "down")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Success rate"
          loading={loading}
          value={fmtPct(team?.success_rate)}
          hint={team ? `${fmtCount(team.success)} of ${fmtCount(team.outcomes)} outcomes` : undefined}
          delta={delta(team?.success_rate, prev?.success_rate, "rate", "up")}
          deltaContext={compareText}
        />
        <KpiTile
          label="Call quality"
          loading={loading}
          value={team?.mos_avg != null ? team.mos_avg.toFixed(2) : "—"}
          hint={team ? `average MOS · ${fmtCount(team.poor_calls)} poor calls` : undefined}
          delta={delta(team?.mos_avg, prev?.mos_avg, "count", "up")}
          deltaContext={compareText}
          info="The switch's MOS (1 to 5) for the rep's side of each call. Under 3.5 is a call people notice."
        />
      </KpiRow>

      <ReportSection
        id="leaderboard"
        title="Leaderboard"
        description="Every rep who worked in the window. Click a header to sort; click a rep for their own report."
        loading={loading}
        exportData={exports.board}
        empty={!loading && reps.length === 0 ? "No rep worked in this window." : false}
        note={filters.campaigns.length || filters.lines.length ? "Calls and sign-ins are narrowed to the chosen lines; time in each state is the rep's across all their lines, which the ACD keeps as one." : undefined}
      >
        <DataTable rows={reps} columns={board} rowKey={(r) => `${r.user}@${r.domain}`} initialSort={{ key: "calls", dir: "desc" }} onRowClick={setOpen} />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="time"
          title="Where the time went"
          description="Each rep's hours, by what they were doing."
          loading={loading}
          exportData={exports.time}
          empty={!loading && reps.length === 0 ? "No rep worked in this window." : false}
        >
          <BarList
            rows={[...reps]
              .sort((a, b) => b.signed_in_seconds - a.signed_in_seconds)
              .map((r) => ({
                key: `${r.user}@${r.domain}`,
                name: repName(r),
                label: repName(r),
                sublabel: `${fmtPct(r.occupancy)} occupied · ${fmtHours(r.signed_in_seconds)} signed in`,
                values: stateGroups(r.state_seconds),
              }))}
            series={STATE_SERIES}
            format={fmtHours}
            ariaLabel="Each rep's time by state"
          />
        </ReportSection>

        <ReportSection
          id="calls-over-time"
          title="Calls taken over time"
          description="The chosen reps together."
          loading={loading}
          empty={!loading && (team?.calls ?? 0) === 0 && reps.length === 0 ? "No rep worked in this window." : false}
        >
          <div className="space-y-6">
            <ColumnChart data={points} series={[{ key: "calls", label: "Calls", slot: 1 }]} height={180} format={fmtCount} ariaLabel="Calls taken per period" />
            <LineChart data={points} series={[{ key: "occupancy", label: "Occupancy", slot: 2 }]} percent area height={150} format={fmtPct} ariaLabel="Occupancy per period" />
          </div>
        </ReportSection>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection id="when-taken" title="When calls were taken" description="On the report's clock." loading={loading}>
          <Heatmap
            cells={(data?.heatmap ?? []).map((c) => ({
              weekday: c.weekday,
              hour: c.hour,
              value: c.calls,
              detail: `${fmtHours(c.talk_total)} talk`,
            }))}
            format={fmtCount}
            legend="More calls"
            ariaLabel="Calls taken by weekday and hour"
          />
        </ReportSection>

        <ReportSection
          id="breaks"
          title="Breaks"
          description="Time on break, by the reason the rep picked."
          loading={loading}
          exportData={exports.breaks}
          empty={!loading && breaks.length === 0 ? "Nobody took a break in this window." : false}
        >
          <BarList
            rows={reps
              .filter((r) => Object.keys(r.break_seconds).length > 0)
              .sort((a, b) => (b.state_seconds.paused ?? 0) - (a.state_seconds.paused ?? 0))
              .map((r) => ({ key: `${r.user}@${r.domain}`, name: repName(r), label: repName(r), values: r.break_seconds }))}
            series={breaks}
            format={fmtSeconds}
            ariaLabel="Each rep's breaks by reason"
          />
        </ReportSection>
      </div>

      <ReportSection
        id="outcomes-by-rep"
        title="Outcomes by rep"
        description="What each rep chose after their calls: the six most common, then the rest."
        loading={loading}
        exportData={exports.outcomes}
        empty={!loading && (team?.outcomes ?? 0) === 0 ? "No outcomes were chosen in this window." : false}
      >
        <DataTable rows={reps.filter((r) => r.outcomes > 0)} columns={outcomeColumns} rowKey={(r) => `${r.user}@${r.domain}`} initialSort={{ key: "outcomes", dir: "desc" }} />
      </ReportSection>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportSection
          id="setters-closers"
          title="Setters and closers"
          description="Handoffs each rep sent and took."
          loading={loading}
          empty={!loading && (team?.handoffs_sent ?? 0) === 0 ? "No handoffs in this window." : false}
        >
          <BarList
            rows={reps
              .filter((r) => r.handoffs_sent + r.handoffs_taken > 0)
              .sort((a, b) => b.handoffs_sent + b.handoffs_taken - (a.handoffs_sent + a.handoffs_taken))
              .map((r) => ({
                key: `${r.user}@${r.domain}`,
                name: repName(r),
                label: repName(r),
                sublabel: r.handoffs_sent ? `${fmtPct(r.handoffs_connected / r.handoffs_sent)} of theirs connected` : undefined,
                values: {
                  sent_connected: r.handoffs_connected,
                  sent_not: r.handoffs_sent - r.handoffs_connected,
                  taken: r.handoffs_taken,
                },
              }))}
            series={[
              { key: "sent_connected", label: "Sent, connected", slot: 1 },
              { key: "sent_not", label: "Sent, not connected", slot: 2 },
              { key: "taken", label: "Taken as closer", slot: 3 },
            ]}
            format={fmtCount}
            ariaLabel="Handoffs sent and taken by each rep"
          />
        </ReportSection>

        <ReportSection
          id="timesheet"
          title="Timesheet"
          description="Per rep per day on the report's clock: first sign-in, last sign-out, time signed in, breaks."
          loading={loading}
          exportData={exports.sheet}
          empty={!loading && (data?.timesheet.length ?? 0) === 0 ? "Nobody was signed in in this window." : false}
        >
          <DataTable
            rows={data?.timesheet ?? []}
            columns={sheetColumns}
            rowKey={(r) => `${r.user}@${r.domain}:${r.day}`}
            initialSort={{ key: "day", dir: "desc" }}
            limit={15}
          />
        </ReportSection>
      </div>

      <RepDrawer rep={open} onClose={() => setOpen(null)} query={query} filters={filters} outcomeNames={outcomeNames} look={look} onDrill={onDrill} />
    </div>
  );
}

/** One rep's own report, over the same filter: a drawer over the leaderboard. */
function RepDrawer({
  rep, onClose, query, filters, outcomeNames, look, onDrill,
}: {
  rep: RepReportRow | null;
  onClose: () => void;
  query: string;
  filters: ReportFilters;
  outcomeNames: Map<string, string>;
  look: (slug: string) => OutcomeLook;
  onDrill: (patch: Partial<ReportFilters>) => void;
}) {
  const id = rep ? `${rep.user}@${rep.domain}` : "";
  const own = React.useMemo(() => {
    const params = new URLSearchParams(query.slice(1));
    params.delete("rep");
    params.delete("skill");
    params.append("rep", id);
    return `?${params.toString()}`;
  }, [query, id]);
  const report = useReport<RepReport>("reps", own, { enabled: Boolean(rep) });
  const data = rep ? report.data : undefined;
  const row = data?.reps.find((r) => `${r.user}@${r.domain}` === id) ?? rep;
  const g = data?.window.granularity ?? "day";
  return (
    <Dialog open={Boolean(rep)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg" variant="drawer">
        <DialogHeader>
          <DialogTitle>{rep ? repName(rep) : ""}</DialogTitle>
          <DialogDescription>
            Extension {rep?.user} · the same window and filters as the report.{" "}
            <button type="button" className="text-accent-11 hover:underline" onClick={() => { onDrill({ reps: [id] }); onClose(); }}>
              Narrow the whole report to this rep
            </button>
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {report.isPending || !row ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-5" />
            </div>
          ) : (
            <>
              <Figures
                items={[
                  { label: "Calls", value: fmtCount(row.calls), hint: `${row.inbound} in · ${row.outbound} out · ${row.handoff} handoff` },
                  { label: "Avg handle", value: fmtSeconds(row.handle_avg), hint: `${fmtSeconds(row.talk_avg)} talk` },
                  { label: "Occupancy", value: fmtPct(row.occupancy) },
                  { label: "Signed in", value: fmtHours(row.signed_in_seconds), hint: row.first_in ? `first ${fmtClock(row.first_in, filters.tz)}` : undefined },
                  {
                    label: "Missed offers",
                    value: fmtCount(row.missed_offers),
                    hint: Object.entries(row.missed_by_reason).map(([k, n]) => `${labelOf(MISSED_LABELS, k)} ${n}`).join(" · ") || undefined,
                  },
                  { label: "Success", value: fmtPct(row.success_rate), hint: `${row.success} of ${row.outcomes} outcomes` },
                  { label: "Hung up first", value: fmtCount(row.hung_up_by_rep), hint: `caller did ${row.hung_up_by_caller}` },
                  { label: "Call quality", value: row.mos_avg != null ? row.mos_avg.toFixed(2) : "—", hint: `${row.poor_calls} poor calls` },
                ]}
              />
              <div>
                <p className="mb-2 text-xs font-medium text-text-muted">Where the time went</p>
                <BarList
                  rows={[{ key: "me", name: repName(row), label: "All lines", values: stateGroups(row.state_seconds) }]}
                  series={STATE_SERIES}
                  format={fmtHours}
                  ariaLabel="Time by state"
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-medium text-text-muted">Calls taken</p>
                <ColumnChart
                  data={(data?.series ?? []).map((s) => ({ key: s.start, label: bucketLabel(s.start, g), title: bucketTitle(s.start, g), values: { calls: s.calls } }))}
                  series={[{ key: "calls", label: "Calls", slot: 1 }]}
                  height={160}
                  format={fmtCount}
                  ariaLabel="This rep's calls per period"
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-medium text-text-muted">When they took calls</p>
                <Heatmap
                  cells={(data?.heatmap ?? []).map((c) => ({ weekday: c.weekday, hour: c.hour, value: c.calls }))}
                  format={fmtCount}
                  legend="More calls"
                  ariaLabel="This rep's calls by weekday and hour"
                />
              </div>
              {Object.keys(row.outcome_mix).length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-text-muted">Their outcomes</p>
                  <BarList
                    rows={Object.entries(row.outcome_mix).map(([slug, n]) => {
                      const group = look(slug).group;
                      return {
                        key: slug,
                        name: outcomeNames.get(slug) ?? slug,
                        label: outcomeNames.get(slug) ?? slug,
                        sublabel: group ? groupTerm(group).label : "No group",
                        values: { [group ?? NO_GROUP]: n },
                      };
                    })}
                    series={groupSeries(Object.keys(row.outcome_mix).map((slug) => look(slug).group))}
                    format={fmtCount}
                    ariaLabel="This rep's outcomes"
                  />
                </div>
              )}
              {(data?.timesheet.length ?? 0) > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium text-text-muted">Days</p>
                  <ul className="divide-y divide-border rounded-md border border-border text-sm">
                    {data!.timesheet
                      .slice()
                      .reverse()
                      .map((d) => (
                        <li key={d.day} className={cn("grid grid-cols-[6rem_1fr_auto] items-center gap-3 px-3 py-1.5")}>
                          <span className="text-text">{d.day}</span>
                          <span className="text-text-muted">
                            {fmtClock(d.first_in, filters.tz)} – {fmtClock(d.last_out, filters.tz)}
                          </span>
                          <span className="tabular-nums text-text-muted">
                            {fmtHours(d.signed_in_seconds)} · {d.calls} calls
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
